import React from 'react'
import CSS from 'csstype'
import * as Lucide from 'lucide-react'
import type { IconNode as LucideIconNode, LucideIcon, LucideProps } from 'lucide-react'

// Every icon in the app comes from this module. The glyphs are Lucide's
// (https://lucide.dev); the exports keep the names the app already uses, so a
// call site never imports lucide-react directly. To add an icon, map a name to
// a Lucide glyph below rather than drawing a new SVG.

export const iconSize = 16
export const iconHeight = iconSize
export const iconWidth = iconSize
// On Lucide's 24px grid, so a 16px icon draws a ~1.33px line.
export const iconStrokeWidth = 2

export type IconProps = Omit<LucideProps, 'ref'>

const baseClassName = 'transition-all shrink-0'

const withBase = (className?: string) => (className ? `${baseClassName} ${className}` : baseClassName)

const icon = (Glyph: LucideIcon, name: string, defaults: IconProps = {}) => {
  const Icon: React.FC<IconProps> = ({ className, ...props }) => (
    // shrink-0: an icon has an intrinsic size. Without it a long label in a
    // flex row squashes the icon to a sliver — the cell menu's "Add Comment /
    // Expand Row" rendered its glyph at 3px.
    <Glyph
      size={iconSize}
      strokeWidth={iconStrokeWidth}
      aria-hidden
      {...defaults}
      {...props}
      className={withBase(className)}
    />
  )
  Icon.displayName = name
  return Icon
}

type Direction = 'left' | 'right' | 'up' | 'down'

interface DirectionalProps extends IconProps {
  type: Direction
}

const directional = (glyphs: Record<Direction, LucideIcon>, name: string) => {
  const Icon: React.FC<DirectionalProps> = ({ type, className, ...props }) => {
    const Glyph = glyphs[type] ?? glyphs.left
    return (
      <Glyph size={iconSize} strokeWidth={iconStrokeWidth} aria-hidden {...props} className={withBase(className)} />
    )
  }
  Icon.displayName = name
  return Icon
}

// Navigation and chrome
export const Search = icon(Lucide.Search, 'Search')
export const SearchLine = icon(Lucide.Search, 'SearchLine')
export const Home = icon(Lucide.House, 'Home')
export const HamburgerMenu = icon(Lucide.Menu, 'HamburgerMenu')
export const PanelLeftLine = icon(Lucide.PanelLeft, 'PanelLeftLine')
export const Navigation = icon(Lucide.ListTree, 'Navigation')
export const WhatsNew = icon(Lucide.Newspaper, 'WhatsNew')
export const Settings = icon(Lucide.Settings, 'Settings')
export const SettingsLine = icon(Lucide.Settings, 'SettingsLine')
export const Dependencies = icon(Lucide.Network, 'Dependencies')
export const Bell = icon(Lucide.Bell, 'Bell')
export const BellLine = icon(Lucide.Bell, 'BellLine')
export const AI = icon(Lucide.Sparkles, 'AI')
export const Sparkles = icon(Lucide.Sparkles, 'Sparkles')
export const SparklesLine = icon(Lucide.Sparkles, 'SparklesLine')
export const LogoutIcon = icon(Lucide.LogOut, 'LogoutIcon')
export const AdminPanel = icon(Lucide.AppWindow, 'AdminPanel')
export const Dashboard = icon(Lucide.LayoutDashboard, 'Dashboard')
export const Sun = icon(Lucide.Sun, 'Sun')
export const Moon = icon(Lucide.Moon, 'Moon')
export const Display = icon(Lucide.Monitor, 'Display')

// Direction
export const Arrow = directional(
  { left: Lucide.ArrowLeft, right: Lucide.ArrowRight, up: Lucide.ArrowUp, down: Lucide.ArrowDown },
  'Arrow'
)
// Disclosure caret on expandable rows (folders, hubs).
export const Triangle = directional(
  { left: Lucide.ChevronLeft, right: Lucide.ChevronRight, up: Lucide.ChevronUp, down: Lucide.ChevronDown },
  'Triangle'
)
export const ChevronDown = icon(Lucide.ChevronDown, 'ChevronDown')
export const ChevronUp = icon(Lucide.ChevronUp, 'ChevronUp')
export const DoubleChevronLeft = icon(Lucide.ChevronsLeft, 'DoubleChevronLeft')
export const DoubleChevronRight = icon(Lucide.ChevronsRight, 'DoubleChevronRight')
export const ExpandAll = icon(Lucide.ChevronsUpDown, 'ExpandAll')
export const CollapseAll = icon(Lucide.ChevronsDownUp, 'CollapseAll')
export const Option = icon(Lucide.ChevronDown, 'Option')

// Actions
export const Plus = icon(Lucide.Plus, 'Plus')
export const ArrowUp = icon(Lucide.ArrowUp, 'ArrowUp')
export const ArrowDown = icon(Lucide.ArrowDown, 'ArrowDown')
export const StopSquare = icon(Lucide.Square, 'StopSquare')
export const Minus = icon(Lucide.Minus, 'Minus')
export const Cross = icon(Lucide.X, 'Cross')
export const Tick = icon(Lucide.Check, 'Tick')
export const Pencil = icon(Lucide.Pencil, 'Pencil')
export const PencilSparkles = icon(Lucide.PencilSparkles, 'PencilSparkles')
export const Delete = icon(Lucide.Trash2, 'Delete')
export const Copy = icon(Lucide.Copy, 'Copy')
export const CopyLink = icon(Lucide.Link, 'CopyLink')
export const Cut = icon(Lucide.Scissors, 'Cut')
export const Paste = icon(Lucide.ClipboardPaste, 'Paste')
export const Clipboard = icon(Lucide.ClipboardCheck, 'Clipboard')
export const Save = icon(Lucide.Save, 'Save')
export const Download = icon(Lucide.FileDown, 'Download')
export const DownloadTray = icon(Lucide.Download, 'DownloadTray')
export const Export = icon(Lucide.FileDown, 'Export')
export const ExportComponent = icon(Lucide.FilePlusCorner, 'ExportComponent')
export const ImportData = icon(Lucide.FilePlusCorner, 'ImportData')
export const SwitchView = icon(Lucide.ArrowLeftRight, 'SwitchView')
// A view's row settings: whether people can add rows, and delete them.
export const AddRows = icon(Lucide.Grid2x2Plus, 'AddRows')
export const DeleteRows = icon(Lucide.Delete, 'DeleteRows')
export const Reset = icon(Lucide.Undo2, 'Reset')
export const Restore = icon(Lucide.RotateCcw, 'Restore')
export const Resend = icon(Lucide.RotateCw, 'Resend')
export const History = icon(Lucide.History, 'History')
export const Backup = icon(Lucide.DatabaseBackup, 'Backup')
export const Archive = icon(Lucide.Archive, 'Archive')
export const Sync = icon(Lucide.Zap, 'Sync')
export const Expand = icon(Lucide.Maximize2, 'Expand')
export const Fullscreen = icon(Lucide.Fullscreen, 'Fullscreen')
export const Move = icon(Lucide.GripVertical, 'Move')
export const MoveColumn = icon(Lucide.ArrowLeftRight, 'MoveColumn')
export const Drag = icon(Lucide.GripVertical, 'Drag')
export const Dots = icon(Lucide.EllipsisVertical, 'Dots')
export const Options = icon(Lucide.Ellipsis, 'Options')
// The table page's view actions: sliders, so it doesn't read as a second ⋯
// under the table's own.
export const ViewActions = icon(Lucide.Settings2, 'ViewActions')
export const Comment = icon(Lucide.MessageSquareText, 'Comment')
export const Colour = icon(Lucide.PaintBucket, 'Colour')
export const Visible = icon(Lucide.Eye, 'Visible')
export const Invisible = icon(Lucide.EyeOff, 'Invisible')
export const Lock = icon(Lucide.Lock, 'Lock')
export const Unlock = icon(Lucide.LockOpen, 'Unlock')
export const Permissions = icon(Lucide.UserLock, 'Permissions')
export const SizeWidthIncrease = icon(Lucide.UnfoldHorizontal, 'SizeWidthIncrease')
export const SizeWidthDecrease = icon(Lucide.FoldHorizontal, 'SizeWidthDecrease')
// A form field's width: the whole row, or one of two columns.
export const FormWidthFull = icon(Lucide.RectangleHorizontal, 'FormWidthFull')
export const FormWidthHalf = icon(Lucide.Columns2, 'FormWidthHalf')

// Table views and toolbar
export const Filter = icon(Lucide.Funnel, 'Filter')
// A filter added, in the same lines as Filter.
export const AddFilter = icon(Lucide.FunnelPlus, 'AddFilter')
export const Focus = icon(Lucide.Crosshair, 'Focus')
export const Sort = icon(Lucide.ArrowUpDown, 'Sort')
// A sort's direction, by what the column holds: letters, numbers, dates, or anything else. Both of a
// pair point down, the reading direction, and only the order beside the arrow flips (A–Z / Z–A).
export const SortAZ = icon(Lucide.ArrowDownAZ, 'SortAZ')
export const SortZA = icon(Lucide.ArrowDownZA, 'SortZA')
export const Sort01 = icon(Lucide.ArrowDown01, 'Sort01')
export const Sort10 = icon(Lucide.ArrowDown10, 'Sort10')
export const SortOldest = icon(Lucide.CalendarArrowUp, 'SortOldest')
export const SortNewest = icon(Lucide.CalendarArrowDown, 'SortNewest')
export const SortAscending = icon(Lucide.ArrowDownNarrowWide, 'SortAscending')
export const SortDescending = icon(Lucide.ArrowDownWideNarrow, 'SortDescending')
// A group's settings: split a multi-select cell's values into a group each.
export const SplitValues = icon(Lucide.Ungroup, 'SplitValues')
export const ExportData = icon(Lucide.FileDown, 'ExportData')
export const Group = icon(Lucide.Group, 'Group')
export const Freeze = icon(Lucide.Snowflake, 'Freeze')
export const Join = icon(Lucide.DatabaseSearch, 'Join')
export const Grid = icon(Lucide.Table, 'Grid')
export const Form = icon(Lucide.ClipboardList, 'Form')
export const Chart = icon(Lucide.ChartLine, 'Chart')
export const Views = icon(Lucide.BookOpen, 'Views')
// The table page's All views button: the menu's lines, unlike any view-row action.
export const AllViews = icon(Lucide.Menu, 'AllViews')

// Column types
export const Text = icon(Lucide.Type, 'Text')
// Text columns: lines of text, where Text (a T) stays for text colour and the like.
export const TextType = icon(Lucide.TextAlignStart, 'TextType')
export const Integer = icon(Lucide.Hash, 'Integer')
export const Percentage = icon(Lucide.Percent, 'Percentage')
export const Time = icon(Lucide.Clock, 'Time')
// Date and date-time columns: a calendar with a clock, so they read as dates; the plain clock is
// left to duration.
export const DateTime = icon(Lucide.CalendarClock, 'DateTime')
export const Calendar = icon(Lucide.Calendar, 'Calendar')
export const EditDate = icon(Lucide.CalendarClock, 'EditDate')
export const EditBy = icon(Lucide.UserPen, 'EditBy')
export const Select = icon(Lucide.CircleChevronDown, 'Select')
export const MultiSelect = icon(Lucide.ListTodo, 'MultiSelect')
export const Link = icon(Lucide.Link, 'Link')
export const Unlink = icon(Lucide.Unlink, 'Unlink')
export const ExternalLink = icon(Lucide.ExternalLink, 'ExternalLink')
export const ArrowUpRight = icon(Lucide.ArrowUpRight, 'ArrowUpRight')
export const Attachment = icon(Lucide.Paperclip, 'Attachment')
export const AttachedFile = icon(Lucide.File, 'AttachedFile')
export const Upload = icon(Lucide.Upload, 'Upload')
export const Tag = icon(Lucide.Tag, 'Tag')
export const Phone = icon(Lucide.Phone, 'Phone')
export const Email = icon(Lucide.Mail, 'Email')
export const Json = icon(Lucide.Braces, 'Json')
export const Vote = icon(Lucide.ThumbsUp, 'Vote')
export const Asterisk = icon(Lucide.Asterisk, 'Asterisk')

// Documents and resources
export const Spreadsheet = icon(Lucide.Sheet, 'Spreadsheet')
// View types: a transpose turns the grid on its side; a matrix is a pivot table.
export const TransposeView = icon(Lucide.RotateCcwSquare, 'TransposeView')
export const MatrixView = icon(Lucide.Table2, 'MatrixView')
export const CalendarView = icon(Lucide.CalendarRange, 'CalendarView')
export const Docs = icon(Lucide.FileText, 'Docs')
export const Process = icon(Lucide.FileText, 'Process')
export const Projects = icon(Lucide.LayoutGrid, 'Projects')
export const Template = icon(Lucide.Shapes, 'Template')
export const Folder = icon(Lucide.Folder, 'Folder')
export const FolderOpen = icon(Lucide.FolderOpen, 'FolderOpen')
export const FolderTree = icon(Lucide.FolderTree, 'FolderTree')
export const FolderInput = icon(Lucide.FolderInput, 'FolderInput')
export const FolderUp = icon(Lucide.FolderUp, 'FolderUp')
export const Files = icon(Lucide.Files, 'Files')
export const Zip = icon(Lucide.FolderArchive, 'Zip')
export const PageBreak = icon(Lucide.SeparatorHorizontal, 'PageBreak')
export const Audit = icon(Lucide.ListClock, 'Audit')

// Rich text
export const Bold = icon(Lucide.Bold, 'Bold')
export const Italic = icon(Lucide.Italic, 'Italic')
export const Underline = icon(Lucide.Underline, 'Underline')
export const AlignLeft = icon(Lucide.TextAlignStart, 'AlignLeft')
export const AlignCenter = icon(Lucide.TextAlignCenter, 'AlignCenter')
export const AlignRight = icon(Lucide.TextAlignEnd, 'AlignRight')
// No numbering on a heading.
export const NoNumbering = icon(Lucide.Ban, 'NoNumbering')
export const Bullet = icon(Lucide.List, 'Bullet')
export const NumberedBullet = icon(Lucide.ListOrdered, 'NumberedBullet')
export const List = icon(Lucide.List, 'List')
export const Highlight = icon(Lucide.Highlighter, 'Highlight')
export const Picture = icon(Lucide.Image, 'Picture')
export const Video = icon(Lucide.Video, 'Video')

// Status and feedback
export const Info = icon(Lucide.Info, 'Info')
export const Help = icon(Lucide.CircleQuestionMark, 'Help')
export const Question = icon(Lucide.CircleQuestionMark, 'Question')
export const ErrorOutline = icon(Lucide.CircleAlert, 'ErrorOutline')
export const Warning = icon(Lucide.TriangleAlert, 'Warning')
export const Subscription = icon(Lucide.CircleArrowUp, 'Subscription')
export const ThumbsUp = icon(Lucide.ThumbsUp, 'ThumbsUp')
export const ThumbsDown = icon(Lucide.ThumbsDown, 'ThumbsDown')
export const NonFilledStar = icon(Lucide.Star, 'NonFilledStar')
export const FilledStar = icon(Lucide.Star, 'FilledStar', { fill: 'rgb(246, 192, 80)', color: 'rgb(246, 192, 80)' })

// People, organisation and account
export const People = icon(Lucide.Users, 'People')
export const Users = icon(Lucide.Users, 'Users')
// Hub settings sections.
export const Palette = icon(Lucide.Palette, 'Palette')
export const Regex = icon(Lucide.Regex, 'Regex')
export const Blank = icon(Lucide.SquareDashed, 'Blank')
// One column's options narrowed by another's value: Edit column's "When Country is France".
export const Dependency = icon(Lucide.Workflow, 'Dependency')
export const TypeToAdd = icon(Lucide.TextCursorInput, 'TypeToAdd')
// A select's linked source: changing it, and leaving it for a list typed in by hand.
export const UpdateSource = icon(Lucide.Columns3Cog, 'UpdateSource')
export const ManualList = icon(Lucide.NotepadText, 'ManualList')
// Whether a linked select's chosen values follow the source when they change in it.
export const SyncValues = icon(Lucide.RefreshCw, 'SyncValues')
export const SyncValuesOff = icon(Lucide.RefreshCwOff, 'SyncValuesOff')
export const FileStack = icon(Lucide.FileStack, 'FileStack')
export const Webhook = icon(Lucide.Webhook, 'Webhook')
export const Key = icon(Lucide.KeyRound, 'Key')
export const Code = icon(Lucide.CodeXml, 'Code')
export const Contributors = icon(Lucide.UsersRound, 'Contributors')
export const Profile = icon(Lucide.User, 'Profile')
export const Organisation = icon(Lucide.Building2, 'Organisation')
export const Community = icon(Lucide.UserGroup, 'Community')
export const Globe = icon(Lucide.Globe, 'Globe')
export const Location = icon(Lucide.MapPin, 'Location')
export const Security = icon(Lucide.ShieldKeyhole, 'Security')
export const MFA = icon(Lucide.ShieldLock, 'MFA')
export const Integrations = icon(Lucide.KeyRound, 'Integrations')

// A file with its type written in the bottom-left corner. Built on Lucide's
// `file` outline, cut away at the corner the way its `file-archive` is, so it
// takes the same size and stroke props as every other icon. textLength keeps
// CSV, PDF and DOC the same width whatever font the platform falls back to.
const FILE_CORNER_OUTLINE: LucideIconNode = [
  [
    'path',
    {
      d: 'M18 22a2 2 0 0 0 2-2V8a2.4 2.4 0 0 0-.706-1.706l-3.588-3.588A2.4 2.4 0 0 0 14 2H6a2 2 0 0 0-2 2v10',
      key: 'outline'
    }
  ],
  ['path', { d: 'M14 2v5a1 1 0 0 0 1 1h5', key: 'fold' }]
]

const fileType = (label: string, name: string) => {
  const Icon: React.FC<IconProps> = ({ className, ...props }) => (
    <Lucide.Icon
      iconNode={FILE_CORNER_OUTLINE}
      size={iconSize}
      strokeWidth={iconStrokeWidth}
      aria-hidden
      {...props}
      className={withBase(className)}
    >
      <text
        x="0.5"
        y="23"
        textLength="16"
        lengthAdjust="spacingAndGlyphs"
        fontSize="10"
        fontWeight="700"
        fontFamily="system-ui, -apple-system, 'Segoe UI', sans-serif"
        fill="currentColor"
        stroke="none"
      >
        {label}
      </text>
    </Lucide.Icon>
  )
  Icon.displayName = name
  return Icon
}

// ".0" for decimal columns, drawn on Lucide's grid so it matches the weight of
// the other column-type icons.
const FLOAT_NODE: LucideIconNode = [
  ['path', { d: 'M5 19h.01', key: 'point' }],
  ['rect', { x: '10', y: '5', width: '9', height: '14', rx: '4.5', key: 'zero' }]
]

const FloatComponent: React.FC<IconProps> = ({ className, ...props }) => (
  <Lucide.Icon
    iconNode={FLOAT_NODE}
    size={iconSize}
    strokeWidth={iconStrokeWidth}
    aria-hidden
    {...props}
    className={withBase(className)}
  />
)

export const Float = FloatComponent

// Up arrow, two lines, down arrow: the drag-to-resize-height handle. Lucide's separator-horizontal
// with the single divider doubled.
const RESIZE_VERTICAL_NODE: LucideIconNode = [
  ['path', { d: 'm8 6 4-4 4 4', key: 'up' }],
  ['path', { d: 'M4 10h16', key: 'line-top' }],
  ['path', { d: 'M4 14h16', key: 'line-bottom' }],
  ['path', { d: 'm16 18-4 4-4-4', key: 'down' }]
]

const ResizeVerticalComponent: React.FC<IconProps> = ({ className, ...props }) => (
  <Lucide.Icon
    iconNode={RESIZE_VERTICAL_NODE}
    size={iconSize}
    strokeWidth={iconStrokeWidth}
    aria-hidden
    {...props}
    className={withBase(className)}
  />
)

export const ResizeVertical = ResizeVerticalComponent

export const Csv = fileType('CSV', 'Csv')
export const Excel = fileType('XLS', 'Excel')
export const Pdf = fileType('PDF', 'Pdf')
export const Word = fileType('DOC', 'Word')

// Glyphs Lucide has no match for: brand marks (Lucide ships no logos) and a
// few table glyphs whose old drawing reads better.
interface SvgProps {
  style?: CSS.Properties
  className?: string
  size?: number | string
  width?: number | string
  height?: number | string
}

const SvgWrapper: React.FC<SvgProps & { viewBox?: string; children: React.ReactNode }> = ({
  children,
  viewBox = '0 0 15 15',
  style,
  className,
  size = iconSize,
  width,
  height
}) => (
  <svg
    className={withBase(className)}
    width={width ?? size}
    height={height ?? size}
    viewBox={viewBox}
    fill="currentColor"
    aria-hidden
    style={{ ...style, shapeRendering: 'geometricPrecision' }}
  >
    {children}
  </svg>
)

// Checkbox columns: Lucide's square-check, outlined at the set's stroke like every other column type
// (it was a filled custom glyph).
export const Checkbox = icon(Lucide.SquareCheck, 'Checkbox')
// Variable columns and resource types: the dollar sign customers know, as Lucide draws it, so it
// matches the set's stroke (the original was a filled custom glyph that read heavier).
export const Variable = icon(Lucide.DollarSign, 'Variable')

const SmallRowHeightComponent: React.FC<SvgProps> = ({ style, className, ...sizing }) => {
  return (
    <SvgWrapper style={style} className={className} {...sizing}>
      <path
        fillRule="evenodd"
        fill="currentColor"
        d="M12.002 11.511v-7h-.626c-.3 0-.479-.37-.312-.645l1.422-1.645a.7.7 0 0 1 1.059 0l1.422 1.645c.166.276-.013.646-.312.646H14v7h.655c.3 0 .478.37.312.645l-1.417 1.677a.7.7 0 0 1-1.07 0l-1.416-1.677c-.167-.276.012-.646.312-.646h.626zM1.5 2h8a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5v-2a.5.5 0 0 1 .5-.5zm0 5h8a.5.5 0 0 1 0 1h-8a.5.5 0 0 1 0-1zm0 3h8a.5.5 0 1 1 0 1h-8a.5.5 0 1 1 0-1zm0 3h8a.5.5 0 1 1 0 1h-8a.5.5 0 1 1 0-1z"
      ></path>
    </SvgWrapper>
  )
}

export const SmallRowHeight = SmallRowHeightComponent

const MediumRowHeightComponent: React.FC<SvgProps> = ({ style, className, ...sizing }) => {
  return (
    <SvgWrapper style={style} className={className} {...sizing}>
      <path
        fillRule="evenodd"
        fill="currentColor"
        d="M12.002 11.511v-7h-.626c-.3 0-.479-.37-.312-.645l1.422-1.645a.7.7 0 0 1 1.059 0l1.422 1.645c.166.276-.013.646-.312.646H14v7h.655c.3 0 .478.37.312.645l-1.417 1.677a.7.7 0 0 1-1.07 0l-1.416-1.677c-.167-.276.012-.646.312-.646h.626zM1.5 2h8a.5.5 0 0 1 .5.5v5.02a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5V2.5a.5.5 0 0 1 .5-.5zm0 8h8a.5.5 0 1 1 0 1h-8a.5.5 0 1 1 0-1zm0 3h8a.5.5 0 1 1 0 1h-8a.5.5 0 1 1 0-1z"
      ></path>
    </SvgWrapper>
  )
}

export const MediumRowHeight = MediumRowHeightComponent

const TallRowHeightComponent: React.FC<SvgProps> = ({ style, className, ...sizing }) => {
  return (
    <SvgWrapper style={style} className={className} {...sizing}>
      <path
        fillRule="evenodd"
        fill="currentColor"
        d="M12.002 11.511v-7h-.626c-.3 0-.479-.37-.312-.645l1.422-1.645a.7.7 0 0 1 1.059 0l1.422 1.645c.166.276-.013.646-.312.646H14v7h.655c.3 0 .478.37.312.645l-1.417 1.677a.7.7 0 0 1-1.07 0l-1.416-1.677c-.167-.276.012-.646.312-.646h.626zM1.5 2h8a.5.5 0 0 1 .5.5v7.979a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5V2.5a.5.5 0 0 1 .5-.5zm0 11h8a.5.5 0 1 1 0 1h-8a.5.5 0 1 1 0-1z"
      ></path>
    </SvgWrapper>
  )
}

export const TallRowHeight = TallRowHeightComponent

const ExtraTallRowHeightComponent: React.FC<SvgProps> = ({ style, className, ...sizing }) => {
  return (
    <SvgWrapper style={style} className={className} {...sizing}>
      <path
        fillRule="evenodd"
        fill="currentColor"
        d="M12.002 11.511v-7h-.626c-.3 0-.479-.37-.312-.645l1.422-1.645a.7.7 0 0 1 1.059 0l1.422 1.645c.166.276-.013.646-.312.646H14v7h.655c.3 0 .478.37.312.645l-1.417 1.677a.7.7 0 0 1-1.07 0l-1.416-1.677c-.167-.276.012-.646.312-.646h.626zM1.5 2h8a.5.5 0 0 1 .5.5v11.023a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5V2.5a.5.5 0 0 1 .5-.5z"
      ></path>
    </SvgWrapper>
  )
}

export const ExtraTallRowHeight = ExtraTallRowHeightComponent

const TwitterComponent: React.FC<SvgProps> = ({ style, className, ...sizing }) => {
  return (
    <SvgWrapper viewBox="0 0 273.5 222.3" style={style} className={className} {...sizing}>
      <path
        d="M273.5 26.3a109.77 109.77 0 0 1-32.2 8.8 56.07 56.07 0 0 0 24.7-31 113.39 113.39 0 0 1-35.7 13.6 56.1 56.1 0 0 0-97 38.4 54 54 0 0 0 1.5 12.8A159.68 159.68 0 0 1 19.1 10.3a56.12 56.12 0 0 0 17.4 74.9 56.06 56.06 0 0 1-25.4-7v.7a56.11 56.11 0 0 0 45 55 55.65 55.65 0 0 1-14.8 2 62.39 62.39 0 0 1-10.6-1 56.24 56.24 0 0 0 52.4 39 112.87 112.87 0 0 1-69.7 24 119 119 0 0 1-13.4-.8 158.83 158.83 0 0 0 86 25.2c103.2 0 159.6-85.5 159.6-159.6 0-2.4-.1-4.9-.2-7.3a114.25 114.25 0 0 0 28.1-29.1"
        fill="currentColor"
      ></path>
    </SvgWrapper>
  )
}

export const Twitter = TwitterComponent

const LinkedInComponent: React.FC<SvgProps> = ({ style, className, ...sizing }) => {
  return (
    <SvgWrapper viewBox="0 0 455 455" style={style} className={className} {...sizing}>
      <path
        d="M0,0v455h455V0H0z M141.522,378.002H74.016V174.906h67.506V378.002z
		 M107.769,147.186h-0.446C84.678,147.186,70,131.585,70,112.085c0-19.928,15.107-35.087,38.211-35.087
		c23.109,0,37.31,15.159,37.752,35.087C145.963,131.585,131.32,147.186,107.769,147.186z M385,378.002h-67.524V269.345
		c0-27.291-9.756-45.92-34.195-45.92c-18.664,0-29.755,12.543-34.641,24.693c-1.776,4.34-2.24,10.373-2.24,16.459v113.426h-67.537
		c0,0,0.905-184.043,0-203.096H246.4v28.779c8.973-13.807,24.986-33.547,60.856-33.547c44.437,0,77.744,29.02,77.744,91.398V378.002
		z"
      />
    </SvgWrapper>
  )
}

export const LinkedIn = LinkedInComponent
